import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Search,
  Plus,
  CheckCircle,
  Calendar,
  Edit,
  Trash2,
  User,
  Clock,
  AlertTriangle,
  Filter,
} from "lucide-react";
import TaskCreateModal from "./Modal/Create";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import TaskEditModal from "./Modal/Edit";

interface Task {
  _id: string;
  title: string;
  description?: string;
  dueDate: string;
  completed: boolean;
  assignedTo?: {
    _id: string;
    username: string;
    email?: string;
  };
  createdAt: string;
  updatedAt: string;
}

type FilterType = "all" | "pending" | "completed" | "overdue";

const Task: React.FC = () => {
  const AppService = useAppServices();
  const { contactId, locationId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedTask, setSelectedTask] = useState<string | null>(null);

  // Utility functions defined first
  const isOverdue = useCallback(
    (dueDate: string, completed: boolean = false) => {
      return !completed && new Date(dueDate) < new Date();
    },
    []
  );

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  // Calculate task counts for filters
  const taskCounts = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.completed).length;
    const overdue = tasks.filter(
      (t) => !t.completed && isOverdue(t.dueDate)
    ).length;
    const pending = total - completed - overdue;

    return { total, completed, overdue, pending };
  }, [tasks, isOverdue]);

  // Filter buttons configuration
  const filterButtons = useMemo(
    () => [
      { key: "all" as FilterType, label: "All", count: taskCounts.total },
      {
        key: "pending" as FilterType,
        label: "Pending",
        count: taskCounts.pending,
      },
      {
        key: "overdue" as FilterType,
        label: "Overdue",
        count: taskCounts.overdue,
      },
      {
        key: "completed" as FilterType,
        label: "Completed",
        count: taskCounts.completed,
      },
    ],
    [taskCounts]
  );

  // Memoized task filtering
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    // Apply search filter
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(lowerSearchTerm) ||
          task.description?.toLowerCase().includes(lowerSearchTerm) ||
          task.assignedTo?.username?.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "pending":
        filtered = filtered.filter(
          (task) => !task.completed && !isOverdue(task.dueDate)
        );
        break;
      case "completed":
        filtered = filtered.filter((task) => task.completed);
        break;
      case "overdue":
        filtered = filtered.filter(
          (task) => !task.completed && isOverdue(task.dueDate)
        );
        break;
      default:
        break;
    }

    return filtered.sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );
  }, [tasks, searchTerm, activeFilter, isOverdue]);

  // Event handlers
  const handleToggleCompleted = useCallback(
    async (taskId: string, current: boolean) => {
      setSelectedTask(taskId);
      try {
        // Simulate API call
        const { response, error } = await AppService.task.update({
          payload: {
            _id: taskId,
            completed: !current,
          },
        });
        if (response) {
          setTasks((prev) =>
            prev.map((t) =>
              t._id === taskId ? { ...t, completed: !current } : t
            )
          );
        }
      } catch (error) {
        console.error("Failed to update task:", error);
      } finally {
        setSelectedTask(null);
      }
    },
    []
  );

  const handleDelete = async (_id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const { response, error } = await AppService.task.delete({
          query: `_id=${_id}`,
        });

        if (response) {
          Swal.fire("Deleted!", "The record has been deleted.", "success");
          // Reload current page after deletion
          getTasks();
        } else {
          Swal.fire(
            "Error!",
            error?.message || "Something went wrong.",
            "error"
          );
        }
      } catch (err: any) {
        Swal.fire("Error!", err.message || "Delete failed.", "error");
      }
    }
  };

  const getTasks = async () => {
    const { response } = await AppService.task.filter({
      query: `contactId=${contactId}`,
    });
    if (response) setTasks(response.data);
    else setTasks([]);
    setLoading(false);
  };
  const getLocationUsers = async () => {
    const { response } = await AppService.user.filter({
      query: `assignedLocations=${locationId}`,
    });
    if (response)
      setUsers(
        response.data?.map((user: any) => ({
          label: user.username,
          value: user._id,
        }))
      );
    else setUsers([]);
    setLoading(false);
  };
  let pageLoad = true;
  const onload = () => {
    if (pageLoad) {
      getTasks();
      getLocationUsers();
      pageLoad = false;
    }
  };
  useEffect(() => {
    onload();
  }, []);
  // Loading state
  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="animate-pulse bg-slate-200 dark:bg-slate-700 rounded-lg h-20"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <CheckCircle className="size-5 text-blue-600" />
            Tasks
            <span className="text-sm font-normal text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">
              {filteredTasks.length}
            </span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage tasks and deadlines
          </p>
        </div>

        <TaskCreateModal handleRefresh={getTasks} users={users} />
      </div>

      {/* Search and Filter Section */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 size-4" />
          <input
            type="text"
            placeholder="Search tasks by title, description, or assignee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>

        <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
          <Filter className="size-4 text-slate-500 mx-2" />
          {filterButtons.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                activeFilter === key
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className="ml-1.5 text-xs bg-slate-200 dark:bg-slate-600 px-1.5 py-0.5 rounded-full">
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-2">
        {filteredTasks.map((task: any) => {
          const overdue = isOverdue(task.dueDate, task.completed);
          const isUpdating = selectedTask === task._id;

          return (
            <div
              key={task._id}
              className={`group bg-white dark:bg-slate-800 rounded-xl border transition-all duration-200 hover:shadow-md ${
                overdue
                  ? "border-red-200 dark:border-red-500/30 bg-red-50/50 dark:bg-red-500/5"
                  : "border-slate-200 dark:border-slate-700"
              } ${isUpdating ? "opacity-60" : "opacity-100"}`}
            >
              <div className="p-4 flex items-start gap-4">
                {/* Completion Toggle */}
                <button
                  onClick={() =>
                    handleToggleCompleted(task._id, task.completed)
                  }
                  disabled={isUpdating}
                  className={`flex-shrink-0 mt-0.5 p-1.5 rounded-full border transition-all ${
                    task.completed
                      ? "bg-green-500 border-green-500 text-white shadow-sm"
                      : "border-slate-300 dark:border-slate-600 text-transparent hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-500/10"
                  } ${isUpdating ? "cursor-not-allowed" : "cursor-pointer"}`}
                  aria-label={
                    task.completed ? "Mark as incomplete" : "Mark as complete"
                  }
                >
                  <CheckCircle className="size-4" />
                </button>

                {/* Task Content */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3
                        className={`text-base font-medium leading-snug truncate ${
                          task.completed
                            ? "text-slate-500 line-through"
                            : "text-slate-900 dark:text-white"
                        }`}
                        title={task.title}
                      >
                        {task.title}
                      </h3>

                      {task.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 mt-1">
                          {task.description}
                        </p>
                      )}
                    </div>

                    {/* Status Badge and Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {overdue && (
                        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 dark:bg-red-500/20 dark:text-red-300 rounded-full">
                          <AlertTriangle className="size-3" />
                          Overdue
                        </span>
                      )}

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <TaskEditModal
                          handleRefresh={getTasks}
                          editData={task}
                          users={users}
                        />
                        {/* <button
                          onClick={() => handleEditTask(task._id)}
                          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="Edit task"
                        >
                          <Edit className="size-4 text-slate-500" />
                        </button> */}
                        <button
                          onClick={() => handleDelete(task._id)}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete task"
                        >
                          <Trash2 className="size-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Meta Information */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                    <span
                      className="flex items-center gap-1.5"
                      title={`Due: ${formatDate(task.dueDate)}`}
                    >
                      <Calendar className="size-4" />
                      {formatDate(task.dueDate)}
                    </span>

                    <span
                      className="flex items-center gap-1.5"
                      title={`Created: ${formatDate(task.createdAt)}`}
                    >
                      <Clock className="size-4" />
                      {formatDate(task.createdAt)}
                    </span>

                    <span
                      className="flex items-center gap-1.5"
                      title={`Assigned to: ${
                        task.assignedTo?.username || "Unassigned"
                      }`}
                    >
                      <User className="size-4" />
                      {task.assignedTo?.username || "Unassigned"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredTasks.length === 0 && (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <div className="size-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center">
              <CheckCircle className="size-6 text-slate-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            {searchTerm || activeFilter !== "all"
              ? "No tasks match your filters"
              : "No tasks yet"}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
            {searchTerm || activeFilter !== "all"
              ? "Try adjusting your search or filter criteria"
              : "Get started by creating your first task to stay organized"}
          </p>
          {searchTerm || activeFilter !== "all" ? (
            <button
              onClick={() => {
                setSearchTerm("");
                setActiveFilter("all");
              }}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Clear filters
            </button>
          ) : (
            <TaskCreateModal handleRefresh={getTasks} users={users} />
          )}
        </div>
      )}
    </div>
  );
};

export default Task;
